import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useI18n, LanguageSwitcher } from '@ah/i18n';
import { useAuthStore } from '../features/auth/auth.store';
import type { AdminOverviewResponse } from '@ah/contracts';
import { useAdminApi } from '../features/overview/useAdminApi';
import { AdminApiError } from '../features/overview/admin.api';

type LoadState = 'loading' | 'loaded' | 'error';

const cardStyle: React.CSSProperties = {
  border: '1px solid var(--ah-color-border, #e2e2e2)',
  borderRadius: 'var(--ah-radius-md, 8px)',
  padding: 'var(--ah-space-4)',
  minWidth: 160,
};

const numberStyle: React.CSSProperties = {
  fontSize: 28,
  fontWeight: 700,
  color: 'var(--ah-color-primary)',
};

const labelStyle: React.CSSProperties = {
  fontSize: 13,
  color: 'var(--ah-color-text-muted)',
};

function StatCard({ label, value, sub }: { label: string; value: string | number; sub?: string }) {
  return (
    <div style={cardStyle}>
      <div style={numberStyle}>{value}</div>
      <div style={labelStyle}>{label}</div>
      {sub && <div style={{ ...labelStyle, marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

/**
 * Admin overview — GET /api/v1/admin/overview (ADMIN role only).
 * Aggregate counts only: no client/therapist identity, no message content
 * (see packages/contracts/src/admin.ts for why — anonymity-first applies
 * here too). Reached only when signed in — <RequireAuth> in App.tsx
 * redirects to /login otherwise.
 */
export default function DashboardPage() {
  const accessToken = useAuthStore((s) => s.tokens?.accessToken);
  const displayName = useAuthStore((s) => s.user?.displayName);
  const signOut = useAuthStore((s) => s.signOut);
  const adminApi = useAdminApi();
  const { t } = useI18n();
  const [state, setState] = useState<LoadState>('loading');
  const [overview, setOverview] = useState<AdminOverviewResponse | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!accessToken) return;
    let cancelled = false;
    setState('loading');
    adminApi
      .getOverview(accessToken)
      .then((result) => {
        if (cancelled) return;
        setOverview(result);
        setState('loaded');
      })
      .catch((err) => {
        if (cancelled) return;
        setErrorMessage(err instanceof AdminApiError ? err.message : t('dashboard.loadError'));
        setState('error');
      });
    return () => {
      cancelled = true;
    };
  }, [accessToken, adminApi, t]);

  return (
    <main style={{ padding: 'var(--ah-space-4)', maxWidth: 960, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <h1 style={{ color: 'var(--ah-color-primary)' }}>{t('dashboard.title')}{displayName ? ` — ${displayName}` : ''}</h1>
        <div style={{ display: 'flex', gap: 'var(--ah-space-3)', alignItems: 'baseline', flexWrap: 'wrap' }}>
          <LanguageSwitcher />
          <Link to="/therapists" style={{ color: 'var(--ah-color-primary)' }}>
            {t('nav.therapists')}
          </Link>
          <Link to="/users" style={{ color: 'var(--ah-color-primary)' }}>
            {t('nav.users')}
          </Link>
          <Link to="/reviews" style={{ color: 'var(--ah-color-primary)' }}>
            {t('nav.reviews')}
          </Link>
          <Link to="/content" style={{ color: 'var(--ah-color-primary)' }}>
            {t('nav.content')}
          </Link>
          <Link to="/specializations" style={{ color: 'var(--ah-color-primary)' }}>
            {t('nav.specializations')}
          </Link>
          <Link to="/media" style={{ color: 'var(--ah-color-primary)' }}>
            {t('nav.media')}
          </Link>
          <Link to="/payments" style={{ color: 'var(--ah-color-primary)' }}>
            {t('nav.payments')}
          </Link>
          <Link to="/reports" style={{ color: 'var(--ah-color-primary)' }}>
            {t('nav.reports')}
          </Link>
          <Link to="/settings/payments" style={{ color: 'var(--ah-color-primary)' }}>
            {t('nav.settings')}
          </Link>
          <Link to="/settings/security" style={{ color: 'var(--ah-color-primary)' }}>
            {t('nav.security')}
          </Link>
          <button onClick={signOut} style={{ background: 'none', border: 'none', color: 'var(--ah-color-text-muted)', cursor: 'pointer' }}>
            {t('nav.signOut')}
          </button>
        </div>
      </div>

      {state === 'loading' && <p role="status">{t('common.loading')}</p>}

      {state === 'error' && <p role="alert">{errorMessage}</p>}

      {state === 'loaded' && overview && (
        <>
          <section style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--ah-space-3)', margin: 'var(--ah-space-4) 0' }}>
            <StatCard label={t('dashboard.totalUsers')} value={overview.users.total} sub={t('dashboard.newLast7Days', { count: overview.users.newLast7Days })} />
            <StatCard label={t('dashboard.clients')} value={overview.users.clients} />
            <StatCard label={t('dashboard.therapists')} value={overview.users.therapists} />
            <StatCard label={t('dashboard.totalBookings')} value={overview.bookings.total} />
            <StatCard
              label={t('dashboard.anonymousVsNamed')}
              value={overview.bookings.anonymousCount}
              sub={t('dashboard.namedSuffix', { count: overview.bookings.standardCount })}
            />
            <StatCard label={t('dashboard.calls')} value={overview.calls.total} />
            <StatCard
              label={t('dashboard.avgCallDuration')}
              value={
                overview.calls.avgDurationSeconds != null
                  ? t('dashboard.minutesSuffix', { minutes: Math.round(overview.calls.avgDurationSeconds / 60) })
                  : '—'
              }
            />
            <StatCard label={t('dashboard.messagesSent')} value={overview.messages.total} />
            <StatCard
              label={t('dashboard.completedRevenue')}
              value={`${overview.revenue.completedGrossTotal} ${overview.revenue.currency}`}
              sub={t('dashboard.platformFeeSuffix', { amount: overview.revenue.platformFeeTotal })}
            />
          </section>

          <section>
            <h2 style={{ fontSize: 18 }}>{t('dashboard.recentBookings')}</h2>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--ah-color-border, #e2e2e2)' }}>
                  <th style={{ padding: 'var(--ah-space-2)' }}>{t('dashboard.scheduled')}</th>
                  <th style={{ padding: 'var(--ah-space-2)' }}>{t('dashboard.status')}</th>
                  <th style={{ padding: 'var(--ah-space-2)' }}>{t('dashboard.mode')}</th>
                </tr>
              </thead>
              <tbody>
                {overview.recentBookings.map((b) => (
                  <tr key={b.id} style={{ borderBottom: '1px solid var(--ah-color-border, #f0f0f0)' }}>
                    <td style={{ padding: 'var(--ah-space-2)' }}>{new Date(b.scheduledAt).toLocaleString()}</td>
                    <td style={{ padding: 'var(--ah-space-2)' }}>{b.status}</td>
                    <td style={{ padding: 'var(--ah-space-2)' }}>{b.callMode}</td>
                  </tr>
                ))}
                {overview.recentBookings.length === 0 && (
                  <tr>
                    <td style={{ padding: 'var(--ah-space-2)' }} colSpan={3}>
                      {t('dashboard.noBookings')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </section>

          <p style={{ ...labelStyle, marginTop: 'var(--ah-space-4)' }}>
            {t('dashboard.generatedAt', { when: new Date(overview.generatedAt).toLocaleString() })}
          </p>
        </>
      )}
    </main>
  );
}
